import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { logWarn } from '@/lib/log';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Cache non-sensitive user profile for instant UI; session is validated via httpOnly cookie.
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('dd_user');
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth, skip the /me check —
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Always validate the session against the server (cookie sent automatically).
    api.get('/auth/me').then((r) => {
      const merged = { ...r.data.user, ...r.data };
      setUser(merged);
      localStorage.setItem('dd_user', JSON.stringify(merged));
    }).catch(() => {
      localStorage.removeItem('dd_user');
      setUser(null);
    }).finally(() => setLoading(false));
  }, []);

  const persist = useCallback((data) => {
    // No token stored client-side; the backend set an httpOnly cookie.
    const u = { ...data.user, customer_id: data.customer_id, driver_id: data.driver_id };
    localStorage.setItem('dd_user', JSON.stringify(u));
    setUser(u);
    return u;
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post('/auth/login', { email, password });
    return persist(r.data);
  }, [persist]);
  const register = useCallback(async (payload) => {
    const r = await api.post('/auth/register', payload);
    return persist(r.data);
  }, [persist]);
  const loginWithData = useCallback((data) => persist(data), [persist]);
  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (e) { logWarn('Logout request failed (continuing local sign-out)', e); }
    localStorage.removeItem('dd_user');
    setUser(null);
    window.location.href = '/login';
  }, []);

  // Memoize the context value so consumers don't re-render on every provider render.
  const value = useMemo(() => ({ user, loading, login, register, loginWithData, logout }),
    [user, loading, login, register, loginWithData, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
